import React, { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Stack, TextField, Switch, FormControlLabel, Typography, Chip, Box } from '@mui/material';
import { NotificationConfig } from '../models';

interface NotificationConfigDialogProps {
    open: boolean;
    config: NotificationConfig | undefined;
    onSave: (config: NotificationConfig) => void;
    onClose: () => void;
}

const NotificationConfigDialog: React.FC<NotificationConfigDialogProps> = ({ open, config, onSave, onClose }) => {
    const [notificationConfig, setNotificationConfig] = useState<NotificationConfig>(
        config || { enabled: false, recipients: [] }
    );
    const [recipientInput, setRecipientInput] = useState('');

    const handleToggleEnabled = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotificationConfig({ ...notificationConfig, enabled: event.target.checked });
    };

    const handleRecipientInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecipientInput(event.target.value);
    };

    const handleAddRecipient = () => {
        const trimmed = recipientInput.trim();
        if (trimmed && !notificationConfig.recipients.includes(trimmed)) {
            setNotificationConfig({
                ...notificationConfig,
                recipients: [...notificationConfig.recipients, trimmed]
            });
            setRecipientInput('');
        }
    };

    const handleDeleteRecipient = (recipient: string) => {
        setNotificationConfig({
            ...notificationConfig,
            recipients: notificationConfig.recipients.filter(r => r !== recipient)
        });
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleAddRecipient();
        }
    };

    const handleSave = () => {
        onSave(notificationConfig);
        onClose();
    };

    const handleCancel = () => {
        setNotificationConfig(config || { enabled: false, recipients: [] });
        setRecipientInput('');
        onClose();
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth>
            <DialogTitle>Signal Notification Settings</DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={3} sx={{ mt: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={notificationConfig.enabled}
                                onChange={handleToggleEnabled}
                            />
                        }
                        label="Enable Signal Notifications"
                    />
                    
                    <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                label="Phone Number"
                                value={recipientInput}
                                onChange={handleRecipientInputChange}
                                onKeyPress={handleKeyPress}
                                fullWidth
                                placeholder="+1234567890"
                                helperText="Enter phone number and press Enter or Add"
                                disabled={!notificationConfig.enabled}
                            />
                            <Button 
                                variant="contained" 
                                onClick={handleAddRecipient}
                                disabled={!notificationConfig.enabled || !recipientInput.trim()}
                                sx={{ minWidth: '80px' }}
                            >
                                Add
                            </Button>
                        </Stack>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {notificationConfig.recipients.map((recipient) => (
                                <Chip
                                    key={recipient}
                                    label={recipient}
                                    onDelete={() => handleDeleteRecipient(recipient)}
                                    disabled={!notificationConfig.enabled}
                                />
                            ))}
                        </Box>
                    </Box>
                    
                    <Typography variant="caption" color="text.secondary">
                        When you confirm a day, a Signal message with all events for that day will be sent to these phone numbers.
                        The message will include emojis and event names.
                    </Typography>
                    
                    <Typography variant="caption" color="primary">
                        Using Signal API: https://hal.fchapleau.com/signal/v2/send
                    </Typography>
                    
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>Save</Button>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default NotificationConfigDialog;
